import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronDown, RotateCcw, FileText, Printer } from 'lucide-react';
import { api } from '../../../api/client';
import { EntityLink } from '../../../components/shared/EntityLink';
import { StatusBadge } from '../components/StatusBadge';
import { ReleasePreviewModal } from '../modals/ReleasePreviewModal';
import { ReleasePdfButtons } from '../components/ReleasePdfButtons';
import { useCan } from '../../../lib/rbac';

export function HistoryTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const can = useCan();
  const canOperate = can('warehouse.operate');
  // Reabrir un lote y re-imprimir a la fuerza son frenos: llave aparte.
  const canForce = can('warehouse.reopen_force');
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  // 2026-09-10 — liberar abre el pre-chequeo por correo (un PDF por correo).
  const [previewBatch, setPreviewBatch] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedItems, setExpandedItems] = useState<any[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemFilter, setItemFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.get(`/warehouse/batches?seller_id=${sellerId}&limit=30`);
      if (res.success) setBatches(res.data || []);
    } catch (err) { console.error('Failed to load history', err); }
    finally { setLoading(false); }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const loadItems = useCallback(async (batchId: number, filter?: string) => {
    setItemsLoading(true);
    try {
      let url = `/warehouse/batch/${batchId}/items?limit=9999`;
      if (filter) url += `&status=${filter}`;
      const res: any = await api.get(url);
      if (res.success) setExpandedItems(res.data || []);
    } catch (err) { console.error('Failed to load batch items', err); }
    finally { setItemsLoading(false); }
  }, []);

  const toggleExpand = (batchId: number) => {
    if (expandedId === batchId) {
      setExpandedId(null);
      setExpandedItems([]);
    } else {
      setExpandedId(batchId);
      setItemFilter('');
      loadItems(batchId);
    }
  };

  useEffect(() => {
    if (expandedId) loadItems(expandedId, itemFilter || undefined);
  }, [itemFilter]);

  if (loading) return <div className="py-8 text-center text-[var(--color-text-secondary)]">{t('common.loading')}</div>;

  return (
    <div className="space-y-2">
      {previewBatch !== null && (
        <ReleasePreviewModal
          batchId={previewBatch}
          sellerId={sellerId}
          canForce={canForce}
          onClose={() => setPreviewBatch(null)}
          onReleased={() => load()}
        />
      )}
      {batches.map((b: any) => {
        const isExpanded = expandedId === b.id;
        const totalItems = b.item_count || b.total_items || 0;
        const closedCount = b.closed_count ?? 0;
        const hasUnclosed = closedCount < totalItems;

        return (
          <div key={b.id} className={`bg-[var(--color-bg-card)] border rounded-lg overflow-hidden ${
            b.status === 'released' ? 'border-green-500/30' : b.status === 'closed' ? 'border-yellow-500/30' : 'border-[var(--color-border)]'
          }`}>
            {/* Header row */}
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
              onClick={() => toggleExpand(b.id)}>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-[var(--color-text)]">#{b.id}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">{new Date(b.batch_date).toLocaleDateString()}</span>
                <StatusBadge status={b.status} />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex gap-4 text-center">
                  <div>
                    <div className="text-sm font-bold font-mono text-[var(--color-text)]">{totalItems}</div>
                    <div className="text-[10px] text-[var(--color-text-secondary)] uppercase">Items</div>
                  </div>
                  <div>
                    <div className={`text-sm font-bold font-mono ${hasUnclosed ? 'text-yellow-500' : 'text-green-500'}`}>{closedCount}</div>
                    <div className="text-[10px] text-[var(--color-text-secondary)] uppercase">Closed</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold font-mono text-[var(--color-text)]">{b.total_orders || 0}</div>
                    <div className="text-[10px] text-[var(--color-text-secondary)] uppercase">Orders</div>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  {b.status === 'closed' && canForce && (
                    <button onClick={async () => { setActionLoading(b.id); try { const res: any = await api.post('/warehouse/batch/' + b.id + '/reopen', { seller_id: sellerId }); if (!res?.success) alert(res?.error || 'No se pudo reabrir el lote'); load(); } catch (e: any) { alert(e?.message || 'No se pudo reabrir el lote'); } finally { setActionLoading(null); } }}
                      disabled={actionLoading === b.id} className="p-1.5 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded text-yellow-600" title={t('wh.reopenBatch')}>
                      <RotateCcw size={14} />
                    </button>
                  )}
                  {b.status === 'closed' && canOperate && (
                    <button onClick={() => setPreviewBatch(b.id)}
                      disabled={actionLoading === b.id} className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/30 rounded text-green-600" title={t('wh.release')}>
                      <FileText size={14} />
                    </button>
                  )}
                  {b.status === 'released' && canOperate && (
                    <ReleasePdfButtons batchId={b.id} sellerId={sellerId} fallbackUrl={b.packing_release_url} size="xs" />
                  )}
                  {b.status === 'released' && canForce && (
                    <button onClick={async () => { setActionLoading(b.id); try { const res: any = await api.post('/warehouse/batch/' + b.id + '/release', { seller_id: sellerId, force: true }); if (res.success && res.url) window.open(`/api/warehouse/labels/${res.url}`, '_blank'); load(); } catch {} finally { setActionLoading(null); } }}
                      disabled={actionLoading === b.id} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-blue-500/10 text-blue-500 hover:bg-blue-500/20">
                      <Printer size={13} /> Re-Release
                    </button>
                  )}
                </div>
                <ChevronDown size={16} className={`text-[var(--color-text-secondary)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
            </div>

            {/* Expanded items */}
            {isExpanded && (
              <div className="border-t border-[var(--color-border)] p-4">
                {/* Status filter pills */}
                <div className="flex items-center gap-1.5 mb-3">
                  {['', 'received', 'packed', 'closed'].map(f => (
                    <button key={f} onClick={() => setItemFilter(f)}
                      className={`px-3 py-1.5 text-xs rounded-lg font-semibold transition-colors ${
                        itemFilter === f ? 'bg-blue-600 text-white' : 'bg-[var(--color-bg)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
                      }`}>{f || 'All'}</button>
                  ))}
                </div>

                {itemsLoading ? (
                  <div className="py-4 text-center text-[var(--color-text-secondary)]"><Loader2 size={16} className="animate-spin inline mr-2" />{t('common.loading')}</div>
                ) : expandedItems.length === 0 ? (
                  <div className="py-4 text-center text-sm text-[var(--color-text-secondary)]">No items</div>
                ) : (
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[var(--color-bg-card)] z-10">
                        <tr className="text-left text-[var(--color-text-secondary)] uppercase text-xs tracking-wider">
                          <th className="pb-2 pr-3" style={{width: 60}}></th>
                          <th className="pb-2 pr-3">Producto</th>
                          <th className="pb-2 pr-3">ML Order</th>
                          <th className="pb-2 pr-3">Shipment</th>
                          <th className="pb-2 pr-3">Shipping</th>
                          <th className="pb-2 pr-3">Source</th>
                          <th className="pb-2 pr-3">Status</th>
                          <th className="pb-2">Tiempo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expandedItems.map((it: any) => {
                          const title = it.display_title || it.title;
                          const secs = it.processing_seconds;
                          const timeStr = secs == null ? '-' : secs < 60 ? `${secs}s` : `${Math.floor(secs/60)}m ${secs%60}s`;
                          return (
                            <tr key={it.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
                              <td className="py-2 pr-3">
                                {it.thumbnail ? <img src={it.thumbnail} className="w-10 h-10 object-contain rounded" alt="" /> : <div className="w-10 h-10 bg-[var(--color-bg)] rounded" />}
                              </td>
                              <td className="py-2 pr-3">
                                <div className="font-mono text-blue-500 text-xs font-bold">{it.asin || '-'}</div>
                                {title && <div className="text-xs text-[var(--color-text-secondary)] truncate max-w-[280px]" title={title}>{title}</div>}
                              </td>
                              <td className="py-2 pr-3 text-xs">
                                {it.ml_order_id ? <EntityLink type="ml_order" value={String(it.ml_order_id)} /> : <span className="text-[var(--color-text-secondary)]">-</span>}
                              </td>
                              <td className="py-2 pr-3 text-xs">
                                {(it.shipment_id || it.ml_shipment_id) ? <EntityLink type="ml_shipment" value={String(it.shipment_id || it.ml_shipment_id)} /> : <span className="text-[var(--color-text-secondary)]">-</span>}
                              </td>
                              <td className="py-2 pr-3"><StatusBadge status={it.ship_status || '-'} /></td>
                              <td className="py-2 pr-3"><span className="text-xs px-1.5 py-0.5 rounded bg-[var(--color-bg)] text-[var(--color-text-secondary)]">{it.source || '-'}</span></td>
                              <td className="py-2 pr-3"><StatusBadge status={it.status} /></td>
                              <td className="py-2 text-xs font-mono text-[var(--color-text-secondary)]">{timeStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
